/**
 * PayK12 Unified HTTP Client
 *
 * Single source of truth for all HTTP communication using ky.
 * Features: Authentication, caching, performance monitoring, error handling, retries.
 *
 * This is the ONLY HTTP client that should be used across the application.
 * All HTTP requests flow through the shared ky instance for consistency.
 */
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import ky, { HTTPError, Options as KyOptions } from 'ky'
import { isEmpty } from 'lodash'
import qs from 'qs'
import { API_HOST, LEGACY_API_HOST } from './constants'
import { logger } from './logger'

// ==================== TYPE DEFINITIONS ====================

export interface ApiError {
  message: string
  code?: string
  status?: number
  details?: unknown
}

export class ApiClientError extends Error {
  public readonly status?: number
  public readonly code?: string
  public readonly details?: unknown

  constructor(error: ApiError) {
    super(error.message)
    this.name = 'ApiClientError'
    this.status = error.status
    this.code = error.code
    this.details = error.details
  }
}

interface RetryOptions {
  limit: number
  methods: string[]
  statusCodes: number[]
  backoffLimit?: number
}

interface PerformanceMetrics {
  requestCount: number
  totalDuration: number
  averageResponseTime: number
  errorCount: number
  lastUpdated: Date
}

interface RequestCache {
  [key: string]: {
    response: unknown
    timestamp: number
    ttl: number
  }
}

interface ApiClientOptions {
  baseUrl?: string
  timeout?: number
  retry?: RetryOptions
  enableCache?: boolean
  cacheTimeout?: number
  enablePerformanceMetrics?: boolean
  hooks?: {
    beforeRequest?: ((request: Request) => void | Promise<void>)[]
    afterResponse?: ((request: Request, options: KyOptions, response: Response) => void | Promise<void>)[]
  }
}

// ==================== SHARED KY INSTANCE CONFIGURATION ====================

const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  limit: 3,
  methods: ['GET', 'PUT', 'DELETE'],
  statusCodes: [408, 413, 429, 500, 502, 503, 504],
  backoffLimit: 20000, // 20 seconds max backoff
}

/**
 * Shared ky instance that all HTTP operations use.
 * This ensures consistent configuration across all HTTP requests.
 */
function createSharedKyInstance(options: ApiClientOptions = {}): typeof ky {
  const retryOptions = { ...DEFAULT_RETRY_OPTIONS, ...options.retry }

  return ky.create({
    prefixUrl: options.baseUrl,
    timeout: options.timeout || 30000,
    retry: {
      limit: retryOptions.limit,
      methods: retryOptions.methods as ('get' | 'put' | 'head' | 'delete' | 'options' | 'trace' | 'post')[],
      statusCodes: retryOptions.statusCodes,
      backoffLimit: retryOptions.backoffLimit,
    },
    hooks: {
      beforeRequest: [
        async (request) => {
          // Add request ID for tracing
          const requestId = crypto.randomUUID()
          request.headers.set('X-Request-ID', requestId)

          // Add authentication token
          const token = await getToken()
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`)
          }

          // Add default headers
          request.headers.set('x-app-id', 'secure.payk12.com')

          // Store start time for performance tracking
          ;(request as Request & { __startTime?: number }).__startTime = Date.now()

          logger.debug('API request started', {
            requestId,
            method: request.method,
            url: request.url,
          })
        },
        ...(options.hooks?.beforeRequest || []),
      ],
      beforeRetry: [
        ({ request, error, retryCount }) => {
          logger.warn('API request retry', {
            requestId: request.headers.get('X-Request-ID'),
            method: request.method,
            url: request.url,
            retryCount,
            error: error?.message,
          })
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          const requestId = request.headers.get('X-Request-ID')

          logger.debug('API request completed', {
            requestId,
            method: request.method,
            url: request.url,
            status: response.status,
          })

          // Handle 401 unauthorized responses with token refresh
          if (response.status === 401) {
            const currentToken = await getToken()

            if (currentToken && !isAnonymousToken(currentToken)) {
              // PK-1316: an AUTHENTICATED session was rejected. Never mint an
              // anonymous token here — that silently downgraded the user's
              // .APISID cookie to anonymous and produced blank pages. Clear the
              // client session and send the user back to login instead.
              logger.warn('Authenticated session rejected with 401; redirecting to login', {
                url: request.url,
              })
              handleExpiredSession()
            } else {
              // Anonymous session — silently mint a fresh anonymous token.
              logger.debug(`Unauthorized request to ${request.url}. Attempting token refresh.`)
              try {
                const newToken = await refreshToken()
                if (newToken) {
                  request.headers.set('Authorization', `Bearer ${newToken}`)
                  return response
                }
              } catch (error) {
                logger.error('Token refresh failed', { error })
              }
            }
          }

          // Handle API errors
          if (!response.ok) {
            let errorData: ApiError

            try {
              const contentType = response.headers.get('content-type')
              if (contentType?.includes('application/json')) {
                const data = (await response.json()) as {
                  message?: string
                  error?: string
                  code?: string
                  details?: unknown
                  errors?: unknown
                }
                errorData = {
                  message: data.message || data.error || `HTTP ${response.status}`,
                  code: data.code,
                  status: response.status,
                  details: data.details || data.errors,
                }
              } else {
                const text = await response.text()
                errorData = {
                  message: text || `HTTP ${response.status}`,
                  status: response.status,
                }
              }
            } catch {
              errorData = {
                message: `HTTP ${response.status}: ${response.statusText}`,
                status: response.status,
              }
            }

            logger.error('API request failed', {
              requestId,
              method: request.method,
              url: request.url,
              status: response.status,
              error: errorData,
            })

            throw new ApiClientError(errorData)
          }

          return response
        },
        ...(options.hooks?.afterResponse || []),
      ],
    },
  })
}

// ==================== TOKEN MANAGEMENT ====================

/**
 * Get authentication token from cookies or headers
 */
async function getToken(): Promise<string | undefined> {
  // Check if we're in a server context where we might have access to headers
  if (typeof window === 'undefined') {
    try {
      const { headers } = await import('next/headers')
      const headersList = await headers()
      const authHeader = headersList.get('authorization')

      if (authHeader && authHeader.startsWith('Bearer ')) {
        return authHeader.substring(7)
      }
    } catch {
      // If we can't access headers, fall back to cookie
    }
  }

  // Fall back to cookie
  const cookie = getCookie('.APISID')
  return cookie
}

/**
 * Check whether a JWT belongs to an anonymous session.
 * Anonymous tokens carry no `uid` claim (mirrors middleware.ts / get-session.ts).
 * Client-side decode only — the server remains the authority on validity.
 */
function isAnonymousToken(token: string | undefined): boolean {
  if (!token) return true
  try {
    const payloadSegment = token.split('.')[1]
    if (!payloadSegment) return true
    const payload = JSON.parse(atob(payloadSegment.replace(/-/g, '+').replace(/_/g, '/')))
    return !payload.uid
  } catch {
    return true
  }
}

/**
 * Handle an expired/rejected authenticated session: clear client session state
 * and redirect to login. No-op on the server. Never overwrites .APISID with an
 * anonymous token (PK-1316).
 */
function handleExpiredSession(): void {
  if (typeof window === 'undefined') return

  deleteCookie('.APISID')

  // Clear the persisted client auth store (dynamic import keeps api-client
  // usable from server modules without pulling the client store into the graph).
  void import('@/stores/auth-session-store')
    .then(({ useAuthSessionStore }) => {
      useAuthSessionStore.getState().clearSession()
    })
    .catch((error) => {
      logger.error('Failed to clear client session after 401', { error })
    })

  if (!window.location.pathname.startsWith('/login')) {
    const returnTo = `${window.location.pathname}${window.location.search}`
    window.location.assign(`/login?return=${encodeURIComponent(returnTo)}`)
  }
}

/**
 * Refresh authentication token.
 * Only used for ANONYMOUS sessions — mints a fresh anonymous token.
 * Authenticated sessions that receive a 401 go through handleExpiredSession instead.
 */
async function refreshToken(): Promise<string | undefined> {
  try {
    const cookie = await ky
      .post('/api/v2/auth/token', {
        json: { IsAnonymous: true },
      })
      .json<string>()

    logger.info('Token refreshed successfully')
    setCookie('.APISID', cookie)
    return cookie
  } catch (error) {
    logger.error('Failed to refresh token', { error })
    deleteCookie('.APISID')
    return undefined
  }
}

/**
 * Clear authentication token
 */
function clearToken(): void {
  deleteCookie('.APISID')
}

// ==================== SHARED INSTANCES ====================

/**
 * Primary shared ky instance for modern API v2 endpoints
 */
export const sharedKyInstance = createSharedKyInstance({
  baseUrl: process.env.NEXT_PUBLIC_API_URL || '/api/v2',
  enablePerformanceMetrics: true,
  timeout: 30000,
})

/**
 * Legacy shared ky instance for legacy API endpoints
 */
export const legacyKyInstance = createSharedKyInstance({
  baseUrl: process.env.NEXT_PUBLIC_LEGACY_API_URL || '/api',
  enablePerformanceMetrics: true,
  timeout: 30000,
})

// ==================== ENHANCED API CLIENT CLASS ====================

/**
 * Enhanced API client that uses the shared ky instances for all HTTP operations.
 * This is the primary interface for making HTTP requests in the application.
 */
export class ApiClient {
  private cache: RequestCache = {}
  private metrics: PerformanceMetrics = {
    requestCount: 0,
    totalDuration: 0,
    averageResponseTime: 0,
    errorCount: 0,
    lastUpdated: new Date(),
  }
  private options: ApiClientOptions
  private pendingRequests = new Map<string, Promise<unknown>>()
  private client: typeof ky

  constructor(options: ApiClientOptions = {}) {
    this.options = {
      enableCache: false,
      cacheTimeout: 300000, // 5 minutes default
      enablePerformanceMetrics: true,
      ...options,
    }

    // Use the appropriate shared ky instance
    this.client = options.baseUrl?.includes('/api/v2') || !options.baseUrl ? sharedKyInstance : legacyKyInstance
  }

  /**
   * Update performance metrics
   */
  private updateMetrics(duration: number, success: boolean): void {
    if (!this.options.enablePerformanceMetrics) return

    this.metrics.requestCount++
    this.metrics.totalDuration += duration
    this.metrics.averageResponseTime = this.metrics.totalDuration / this.metrics.requestCount
    this.metrics.lastUpdated = new Date()

    if (!success) {
      this.metrics.errorCount++
    }
  }

  /**
   * Generate cache key for request
   */
  private getCacheKey(method: string, url: string, params?: unknown): string {
    return `${method}:${url}:${JSON.stringify(params || {})}`
  }

  /**
   * Check cache for existing response
   */
  private getCachedResponse<T>(cacheKey: string): T | null {
    if (!this.options.enableCache) return null

    const cached = this.cache[cacheKey]
    if (!cached) return null

    const now = Date.now()
    if (now - cached.timestamp > cached.ttl) {
      delete this.cache[cacheKey]
      return null
    }

    return cached.response as T
  }

  /**
   * Store response in cache
   */
  private setCachedResponse<T>(cacheKey: string, response: T, ttl?: number): void {
    if (!this.options.enableCache) return

    this.cache[cacheKey] = {
      response,
      timestamp: Date.now(),
      ttl: ttl || this.options.cacheTimeout || 300000,
    }
  }

  /**
   * Request deduplication for identical concurrent requests
   */
  private async dedupeRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key) as Promise<T>
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key)
    })

    this.pendingRequests.set(key, promise)
    return promise
  }

  /**
   * Build URL with optional proxy routing logic
   */
  private buildUrl(path: string, proxy = false, legacy = false): string {
    let url = path
    if (path.startsWith('/')) {
      if (proxy && path.startsWith('/api')) {
        url = path
      } else if (proxy) {
        url = legacy ? `/api${path}` : `/api/v2${path}`
      } else {
        url = legacy ? `${LEGACY_API_HOST}${path}` : `${API_HOST}${path}`
      }
    }
    return url
  }

  // ==================== HTTP METHODS ====================

  /**
   * GET request with caching and deduplication support
   */
  async get<T>(
    url: string,
    params?: Record<string, unknown> | null,
    options?: { proxy?: boolean; legacy?: boolean; cacheTtl?: number } & KyOptions,
  ): Promise<T> {
    const startTime = Date.now()
    try {
      const { proxy = false, legacy = false, cacheTtl, ...kyOptions } = options || {}
      const fullUrl = this.buildUrl(url, proxy, legacy)
      let queryUrl = fullUrl

      // Add query parameters
      if (!isEmpty(params)) {
        queryUrl = `${fullUrl}?${qs.stringify(params)}`
      }

      // Check cache first
      const cacheKey = this.getCacheKey('GET', queryUrl, params)
      if (this.options.enableCache) {
        const cached = this.getCachedResponse<T>(cacheKey)
        if (cached) {
          return cached
        }
      }

      // Deduplicate concurrent identical requests
      const dedupedRequest = async (): Promise<T> => {
        const client = legacy ? legacyKyInstance : sharedKyInstance
        const response = await client.get(queryUrl, kyOptions).json<T>()

        // Cache successful GET responses
        if (this.options.enableCache) {
          this.setCachedResponse(cacheKey, response, cacheTtl)
        }

        return response
      }

      const result = await this.dedupeRequest(cacheKey, dedupedRequest)
      this.updateMetrics(Date.now() - startTime, true)
      return result
    } catch (error) {
      this.updateMetrics(Date.now() - startTime, false)
      throw this.handleError(error)
    }
  }

  /**
   * POST request with form data support
   */
  async post<T>(
    url: string,
    data?: unknown,
    options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
  ): Promise<T> {
    const startTime = Date.now()
    try {
      const { proxy = false, legacy = false, isForm = false, ...kyOptions } = options || {}
      const fullUrl = this.buildUrl(url, proxy, legacy)

      const requestOptions: KyOptions = { ...kyOptions }

      if (isForm && data && typeof data === 'object') {
        requestOptions.body = qs.stringify(data as Record<string, unknown>)
        requestOptions.headers = {
          ...requestOptions.headers,
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      } else {
        requestOptions.json = data
      }

      const client = legacy ? legacyKyInstance : sharedKyInstance
      const response = await client.post(fullUrl, requestOptions).json<T>()

      this.updateMetrics(Date.now() - startTime, true)
      return response
    } catch (error) {
      this.updateMetrics(Date.now() - startTime, false)
      throw this.handleError(error)
    }
  }

  /**
   * PUT request with form data support
   */
  async put<T>(
    url: string,
    data?: unknown,
    options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
  ): Promise<T> {
    const startTime = Date.now()
    try {
      const { proxy = false, legacy = false, isForm = false, ...kyOptions } = options || {}
      const fullUrl = this.buildUrl(url, proxy, legacy)

      const requestOptions: KyOptions = { ...kyOptions }

      if (isForm && data && typeof data === 'object') {
        requestOptions.body = qs.stringify(data as Record<string, unknown>)
        requestOptions.headers = {
          ...requestOptions.headers,
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      } else {
        requestOptions.json = data
      }

      const client = legacy ? legacyKyInstance : sharedKyInstance
      const response = await client.put(fullUrl, requestOptions).json<T>()

      this.updateMetrics(Date.now() - startTime, true)
      return response
    } catch (error) {
      this.updateMetrics(Date.now() - startTime, false)
      throw this.handleError(error)
    }
  }

  /**
   * DELETE request with query param and form data support
   */
  async delete<T>(
    url: string,
    data?: Record<string, unknown> | null,
    options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
  ): Promise<T> {
    const startTime = Date.now()
    try {
      const { proxy = false, legacy = false, isForm = false, ...kyOptions } = options || {}
      let fullUrl = this.buildUrl(url, proxy, legacy)

      const requestOptions: KyOptions = { ...kyOptions }

      if (isForm && data) {
        requestOptions.body = qs.stringify(data)
        requestOptions.headers = {
          ...requestOptions.headers,
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      } else if (data && !isForm) {
        // Add as query parameters for non-form DELETE requests
        fullUrl = `${fullUrl}?${qs.stringify(data)}`
      }

      const client = legacy ? legacyKyInstance : sharedKyInstance
      const response = await client.delete(fullUrl, requestOptions).json<T>()

      this.updateMetrics(Date.now() - startTime, true)
      return response
    } catch (error) {
      this.updateMetrics(Date.now() - startTime, false)
      throw this.handleError(error)
    }
  }

  /**
   * PATCH request with form data support
   */
  async patch<T>(
    url: string,
    data?: unknown,
    options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
  ): Promise<T> {
    const startTime = Date.now()
    try {
      const { proxy = false, legacy = false, isForm = false, ...kyOptions } = options || {}
      const fullUrl = this.buildUrl(url, proxy, legacy)

      const requestOptions: KyOptions = { ...kyOptions }

      if (isForm && data && typeof data === 'object') {
        requestOptions.body = qs.stringify(data as Record<string, unknown>)
        requestOptions.headers = {
          ...requestOptions.headers,
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      } else {
        requestOptions.json = data
      }

      const client = legacy ? legacyKyInstance : sharedKyInstance
      const response = await client.patch(fullUrl, requestOptions).json<T>()

      this.updateMetrics(Date.now() - startTime, true)
      return response
    } catch (error) {
      this.updateMetrics(Date.now() - startTime, false)
      throw this.handleError(error)
    }
  }

  // ==================== LEGACY FORM METHODS ====================

  /**
   * POST form data returning raw Response
   */
  async postForm(
    path: string,
    payload: Record<string, unknown>,
    proxy = false,
    options: Partial<RequestInit> = {},
  ): Promise<Response> {
    try {
      const fullUrl = this.buildUrl(path, proxy)
      const body = qs.stringify(payload)

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${await getToken()}`,
          'x-app-id': 'secure.payk12.com',
          ...(options.headers as Record<string, string>),
        },
        body,
        ...options,
      })

      return response
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * PUT form data returning raw Response
   */
  async putForm(path: string, payload: Record<string, unknown>, proxy = false): Promise<Response> {
    try {
      const fullUrl = this.buildUrl(path, proxy)
      const body = qs.stringify(payload)

      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${await getToken()}`,
          'x-app-id': 'secure.payk12.com',
        },
        body,
      })

      return response
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * DELETE form data returning raw Response
   */
  async deleteForm(path: string, payload: Record<string, unknown>, proxy = false): Promise<Response> {
    try {
      const fullUrl = this.buildUrl(path, proxy)
      const body = qs.stringify(payload)

      const response = await fetch(fullUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${await getToken()}`,
          'x-app-id': 'secure.payk12.com',
        },
        body,
      })

      return response
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetrics {
    return { ...this.metrics }
  }

  /**
   * Clear performance metrics
   */
  clearPerformanceMetrics(): void {
    this.metrics = {
      requestCount: 0,
      totalDuration: 0,
      averageResponseTime: 0,
      errorCount: 0,
      lastUpdated: new Date(),
    }
  }

  /**
   * Clear request cache
   */
  clearCache(): void {
    this.cache = {}
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    const keys = Object.keys(this.cache)
    return {
      size: keys.length,
      keys,
    }
  }

  /**
   * Enable or disable caching
   */
  setCacheEnabled(enabled: boolean): void {
    this.options.enableCache = enabled
    if (!enabled) {
      this.clearCache()
    }
  }

  /**
   * Enable or disable performance monitoring
   */
  setPerformanceMetricsEnabled(enabled: boolean): void {
    this.options.enablePerformanceMetrics = enabled
    if (!enabled) {
      this.clearPerformanceMetrics()
    }
  }

  /**
   * Handle and transform errors
   */
  private handleError(error: unknown): Error {
    if (error instanceof ApiClientError) {
      return error
    }

    if (error instanceof HTTPError) {
      return error
    }

    if (error instanceof Error) {
      logger.error('Unexpected API error', { error: error.message })
      return new ApiClientError({
        message: error.message || 'An unexpected error occurred',
      })
    }

    logger.error('Unknown API error', { error })
    return new ApiClientError({
      message: 'An unknown error occurred',
    })
  }
}

// ==================== FACTORY FUNCTIONS AND INSTANCES ====================

/**
 * Factory function for creating API clients
 */
export function createApiClient(options?: ApiClientOptions): ApiClient {
  return new ApiClient(options)
}

/**
 * Primary API client instance for modern API v2 endpoints
 */
export const apiClient = createApiClient({
  baseUrl: process.env.NEXT_PUBLIC_API_URL || '/api/v2',
  enablePerformanceMetrics: true,
  enableCache: false, // Disabled by default for safety
  timeout: 30000,
})

/**
 * Legacy API client instance for legacy endpoints
 */
export const legacyApiClient = createApiClient({
  baseUrl: process.env.NEXT_PUBLIC_LEGACY_API_URL || '/api',
  enablePerformanceMetrics: true,
  enableCache: false,
  timeout: 30000,
})

/**
 * High-performance API client with caching enabled
 */
export const cachedApiClient = createApiClient({
  baseUrl: process.env.NEXT_PUBLIC_API_URL || '/api/v2',
  enablePerformanceMetrics: true,
  enableCache: true,
  cacheTimeout: 300000, // 5 minutes
  timeout: 30000,
})

// ==================== CONVENIENCE FUNCTIONS ====================

/**
 * Simple GET request using shared ky instance
 */
export const get = async <T>(
  url: string,
  params?: Record<string, unknown> | null,
  options?: { proxy?: boolean; legacy?: boolean } & KyOptions,
): Promise<T> => {
  return apiClient.get<T>(url, params, options)
}

/**
 * Simple POST request using shared ky instance
 */
export const post = async <T>(
  url: string,
  data?: unknown,
  options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
): Promise<T> => {
  return apiClient.post<T>(url, data, options)
}

/**
 * Simple PUT request using shared ky instance
 */
export const put = async <T>(
  url: string,
  data?: unknown,
  options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
): Promise<T> => {
  return apiClient.put<T>(url, data, options)
}

/**
 * Simple DELETE request using shared ky instance
 */
export const del = async <T>(
  url: string,
  data?: Record<string, unknown> | null,
  options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
): Promise<T> => {
  return apiClient.delete<T>(url, data, options)
}

/**
 * Simple PATCH request using shared ky instance
 */
export const patch = async <T>(
  url: string,
  data?: unknown,
  options?: { proxy?: boolean; legacy?: boolean; isForm?: boolean } & KyOptions,
): Promise<T> => {
  return apiClient.patch<T>(url, data, options)
}

// ==================== FORM DATA CONVENIENCE FUNCTIONS ====================

/**
 * POST form data using shared ky instance
 */
export const postForm = async (
  path: string,
  payload: Record<string, unknown>,
  proxy = false,
  options: Partial<RequestInit> = {},
): Promise<Response> => {
  return apiClient.postForm(path, payload, proxy, options)
}

/**
 * PUT form data using shared ky instance
 */
export const putForm = async (path: string, payload: Record<string, unknown>, proxy = false): Promise<Response> => {
  return apiClient.putForm(path, payload, proxy)
}

/**
 * DELETE form data using shared ky instance
 */
export const deleteForm = async (path: string, payload: Record<string, unknown>, proxy = false): Promise<Response> => {
  return apiClient.deleteForm(path, payload, proxy)
}

// ==================== TOKEN MANAGEMENT EXPORTS ====================

export const tokenUtils = {
  getToken,
  refreshToken,
  clearToken,
}

export { getToken, refreshToken, clearToken }

// ==================== URL UTILITIES ====================

/**
 * Get API domain
 */
export const getDomain = () => API_HOST

/**
 * Get legacy API domain
 */
export const getLegacyDomain = () => LEGACY_API_HOST

/**
 * Build URL with routing logic
 */
export const getUrl = (path: string, proxy: boolean = false, legacy: boolean = false) => {
  let url = `${path}`
  if (path.startsWith('/')) {
    if (proxy && path.startsWith('/api')) {
      url = `${path}`
    } else if (proxy) {
      url = legacy ? `/api${path}` : `/api/v2${path}`
    } else {
      url = legacy ? `${LEGACY_API_HOST}${path}` : `${API_HOST}${path}`
    }
  }
  return url
}

// ==================== KY INSTANCE COMPATIBILITY ====================

/**
 * Direct access to the shared ky instance for advanced use cases
 * This maintains the ky API while using the shared configured instance
 */
export const api = {
  get: <T>(url: string, options?: KyOptions) => {
    const promise = sharedKyInstance.get(url, options) as Promise<Response> & {
      json<R = T>(): Promise<R>
    }
    return promise
  },
  post: <T>(url: string, options?: KyOptions) => {
    const promise = sharedKyInstance.post(url, options) as Promise<Response> & {
      json<R = T>(): Promise<R>
    }
    return promise
  },
  put: <T>(url: string, options?: KyOptions) => {
    const promise = sharedKyInstance.put(url, options) as Promise<Response> & {
      json<R = T>(): Promise<R>
    }
    return promise
  },
  delete: <T>(url: string, options?: KyOptions) => {
    const promise = sharedKyInstance.delete(url, options) as Promise<Response> & {
      json<R = T>(): Promise<R>
    }
    return promise
  },
  patch: <T>(url: string, options?: KyOptions) => {
    const promise = sharedKyInstance.patch(url, options) as Promise<Response> & {
      json<R = T>(): Promise<R>
    }
    return promise
  },
}

// ==================== AXIOS COMPATIBILITY (DEPRECATED) ====================

/**
 * @deprecated Use the direct methods or ApiClient class instead
 * Axios-compatible wrapper for migration purposes
 */
export const axiosCompatibility = {
  get: async <T>(url: string, config?: KyOptions) => {
    const data = await get<T>(url, null, config)
    return { data, status: 200, statusText: 'OK', headers: {} }
  },
  post: async <T>(url: string, data?: unknown, config?: KyOptions) => {
    const responseData = await post<T>(url, data, config)
    return { data: responseData, status: 200, statusText: 'OK', headers: {} }
  },
  put: async <T>(url: string, data?: unknown, config?: KyOptions) => {
    const responseData = await put<T>(url, data, config)
    return { data: responseData, status: 200, statusText: 'OK', headers: {} }
  },
  delete: async <T>(url: string, config?: KyOptions) => {
    const responseData = await del<T>(url, null, config)
    return { data: responseData, status: 200, statusText: 'OK', headers: {} }
  },
  patch: async <T>(url: string, data?: unknown, config?: KyOptions) => {
    const responseData = await patch<T>(url, data, config)
    return { data: responseData, status: 200, statusText: 'OK', headers: {} }
  },
}

// ==================== PERFORMANCE MONITORING ====================

/**
 * Get performance metrics from all clients
 */
export const getPerformanceMetrics = () => {
  return {
    primary: apiClient.getPerformanceMetrics(),
    legacy: legacyApiClient.getPerformanceMetrics(),
  }
}

/**
 * Clear all caches
 */
export const clearAllCaches = () => {
  apiClient.clearCache()
  legacyApiClient.clearCache()
}

/**
 * Get cache statistics for all clients
 */
export const getAllCacheStats = () => {
  return {
    primary: apiClient.getCacheStats(),
    legacy: legacyApiClient.getCacheStats(),
  }
}
